'use client';

import React, { useEffect, useRef, useState } from 'react';

import { ArrowLeftIcon } from '@depop/web-ui-kit/Icons/ArrowLeftIcon';
import { ArrowRightIcon } from '@depop/web-ui-kit/Icons/ArrowRightIcon';
import { clsx } from 'clsx';
import { useSwipeable } from 'react-swipeable';

import { CarouselIndicators } from './CarouselIndicators';
import styles from './styles.module.css';

import { getAltText } from '@/modules/images/helpers';
import { useProductBySlugBasicPDP } from '@/modules/product/hooks/useProductBySlugBasicPDP';
import { useProductBySlugExtendedPDP } from '@/modules/product/hooks/useProductBySlugExtendedPDP';
import { useProductTracking } from '@/modules/product/hooks/useProductTracking';
import { type ProductCarouselInteraction } from '@/modules/product/types';

type MediaCarouselProps = {
  children: React.ReactNode[] | React.ReactNode;
  className?: string;
  imageThumbnails?: string[];
  productId?: number;
  accessory?: React.ReactNode;
  hideAccessory?: (index: number, totalSlides: number) => boolean;
  activeIndex?: number;
  onIndexChange?: (index: number) => void;
  slidesVisible?: number;
  trackImageCarouselIndex?: boolean;
};

export function MediaCarousel({
  children,
  className,
  imageThumbnails = [],
  productId,
  accessory,
  hideAccessory = () => false,
  activeIndex,
  onIndexChange,
  slidesVisible: requestedSlidesVisible = 1,
  trackImageCarouselIndex = false,
}: MediaCarouselProps) {
  const [internalIndex, setInternalIndex] = useState(0);

  const slideCount = React.Children.count(children);

  const slidesVisible = Math.min(
    requestedSlidesVisible,
    Math.max(slideCount, 1)
  );

  const maxIndex = Math.max(0, slideCount - slidesVisible);
  const index = Math.min(activeIndex ?? internalIndex, maxIndex);

  function setIndex(newIndex: number) {
    setInternalIndex(newIndex);
    onIndexChange?.(newIndex);
  }
  const { sendProductInteractionActionATEvent } = useProductTracking();
  const { data: productExtended } = useProductBySlugExtendedPDP();
  const { data: productBasic } = useProductBySlugBasicPDP();
  const sliderRef = useRef<HTMLDivElement>(null);
  const productTitle = productExtended?.attributes?.product_title || '';

  const slides = React.Children.toArray(
    React.Children.map(children, (child, cloneIndex) =>
      // eslint-disable-next-line
      React.cloneElement(child as React.ReactElement<any>, {
        index,
        role: 'group',
        imgsrc: imageThumbnails[cloneIndex],
        'aria-roledescription': 'slide',
        'aria-hidden':
          cloneIndex < index || cloneIndex >= index + slidesVisible,
        'aria-label': `${cloneIndex + 1} of ${slideCount}`,
      })
    )
  );
  const currentSlide = index + 1;
  const slideLength = slides.length;
  const showAccessory = accessory && !hideAccessory(index, slideLength);

  function sendProductCarouselEvent(
    interactionType: ProductCarouselInteraction
  ) {
    if (productId) {
      sendProductInteractionActionATEvent(
        productId,
        interactionType,
        trackImageCarouselIndex ? { imageCarouselIndex: index } : undefined
      );
    }
  }

  const isMultiSlide = slidesVisible > 1;

  function goNextSlide(isSwipe: boolean) {
    setIndex(index >= maxIndex ? 0 : index + 1);
    sendProductCarouselEvent(
      isSwipe ? 'ProductImageSwipeRight' : 'ProductImageArrowRight'
    );
  }

  function goPreviousSlide(isSwipe: boolean) {
    setIndex(index <= 0 ? maxIndex : index - 1);
    sendProductCarouselEvent(
      isSwipe ? 'ProductImageSwipeLeft' : 'ProductImageArrowLeft'
    );
  }

  const handlers = useSwipeable({
    onSwipedLeft: () => {
      goNextSlide(true);
    },
    onSwipedRight: () => {
      goPreviousSlide(true);
    },
  });

  useEffect(() => {
    // Reset to first image when the product changes
    setIndex(0);
  }, [productId]);

  useEffect(() => {
    const altText = productTitle ?? getAltText(productBasic);

    if (altText) {
      const images = sliderRef?.current?.querySelectorAll('img') || [];

      /**
       * Here we are hydrating the "alt" attribute once data is available.
       * We prioritize product_title from Extended response, but fall back to basic product data
       * (sellerUserName, or brandName) to ensure images always have accessible alt text.
       */
      if (images.length) {
        images.forEach((image, idx) => {
          const newAlt = idx === 0 ? altText : `${altText} (${idx + 1})`;
          image.setAttribute('alt', newAlt);
        });
      }
    }
  }, [productTitle, productBasic]);

  return (
    <div
      className={clsx(styles.carouselContainer, className)}
      role="region"
      aria-label="Media carousel"
    >
      <div className={styles.mediaContainer}>
        {showAccessory ? accessory : null}
        <div {...handlers}>
          <div
            role="slider"
            aria-roledescription="carousel"
            aria-live="polite"
            aria-valuenow={currentSlide}
            aria-valuemin={1}
            aria-valuemax={slideLength}
            className={clsx(
              styles.carousel,
              isMultiSlide && styles.carouselMultiSlide
            )}
            style={
              isMultiSlide
                ? ({
                    '--slide-index': index,
                    '--slides-visible': slidesVisible,
                  } as React.CSSProperties)
                : {
                    transform: index
                      ? `translateX(calc(100% * ${index} * -1))`
                      : 'translateX(0)',
                  }
            }
            ref={sliderRef}
          >
            {slides}
          </div>
        </div>
        {slideLength > slidesVisible && (
          <>
            <button
              className={clsx(styles.carouselAction, styles.carouselActionLeft)}
              aria-label="Previous slide"
              onClick={() => goPreviousSlide(false)}
            >
              <div
                className={clsx(
                  styles.carouselActionContainer,
                  styles.carouselActionContainerLeft
                )}
              >
                <ArrowLeftIcon size={20} />
              </div>
            </button>

            <button
              className={clsx(
                styles.carouselAction,
                styles.carouselActionRight
              )}
              aria-label="Next slide"
              onClick={() => goNextSlide(false)}
            >
              <div
                className={clsx(
                  styles.carouselActionContainer,
                  styles.carouselActionContainerRight
                )}
              >
                <ArrowRightIcon size={20} />
              </div>
            </button>
          </>
        )}
      </div>
      <>
        {slideLength <= 1 && <div className={styles.emptyCarouselSpace} />}
        {slideLength > 1 && !isMultiSlide && (
          <CarouselIndicators
            currentSlide={currentSlide}
            index={index}
            slides={slides}
          />
        )}
      </>
    </div>
  );
}
